<template>
  <div class="container">
    <div class="links-building">
      <div class="links-building__title">
        <div @click="exportCsv" v-if="isExportButtonVisible">
          <base-material-design-icon icon="download"/>
        </div>
      </div>
      <div class="links-building__loader" v-if="!isDataFetched">
        <base-loader/>
      </div>
      <div class="links-building__no-data" v-else-if="isNoData">
        No Data: Audiencekey supplies marketing content. For link building capabilities, contact your analyst.
      </div>
      <div class="links-building__wrapper" v-else>
        <div class="links-building__header">
          <base-smart-input-search :options="urls"
                            ref="search"
                            :selectStyle="{height: '60px!important'}"
                            :borderLess="false"
                            v-model="filters.searchUrl"
                            @apply="onSearch"
                            placeholder="Search by URL">
            <base-material-design-icon class="links-building__search" icon="search" v-if="!filters.searchUrl.value"/>
            <div class="links-building__clear-button" v-else @click.stop="clearSearch"/>
          </base-smart-input-search>
          <base-check-box class="links-building__checkbox" label="Is Active" v-model="filters.isActiveState" @change="updateFilteredList"/>
          <base-multi-select-sortable-header
            class="links-building__date-sort"
            :column="dataSortConfig"
            :sort-config="sortConfig"
            @sort="onSortByDate($event, dataSortConfig)"
            @apply="onFilterByDate($event)"
            @cancel="onFilterByDate($event)"
          />
        </div>

        <base-chips v-if="filters.contentBriefId" :label="`${contentBriefName} - Created Links`" @click="clearContentBriefFilter"/>

        <div v-resize @resize="resizeHandler">
          <div class="links-building__grid" ref="grid">
            <link-building-item class="links-building__item"
                                v-for="link in paginatedLinks"
                                :link="link"
                                :key="link._id"/>
          </div>
        </div>
        <div class="links-building__pagination-wrapper" v-show="hasPagination">
          <base-pagination :limit="pageItemsLimit"
                           :total-items="totalItems"
                           @pageChanged="pageChanged"/>
        </div>
      </div>
      <div class="links-building__update-loader" v-if="isDataUpdating">
        <base-loader class="links-building__update-loader-ring"/>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable no-unused-vars */

import { mapActions, mapState } from 'vuex'
import LinkBuildingItem from './LinkBuildingItem'
import helper from '@/services/Helper'
import csvExport from '@/services/CSVExport'
import _ from 'lodash'
import moment from 'moment'
import filters from '@/common/Filters'
import BaseMultiSelectSortableHeader from '../../common/BaseMultiSelectSortableHeader'
import BaseChips from "../../common/BaseChips";

const emptyFilters = {
  searchUrl: {},
  date: null,
  dateFrom: null,
  dateTo: null,
  isActiveState: false,
  contentBriefId: null
}
const ITEM_NUMBER_FULL = 15
const ITEM_NUMBER_SHRINK = 12
/**
 * Link Building component contains link grid with sorting/filtering functionality
 */
export default {
  name: 'LinkBuilding',
  components: { BaseChips, LinkBuildingItem },
  props: ['content-brief'],
  created () {
    if (this.selectedClient) {
      this.loadData()
    }
  },
  mounted () {
    if (this.contentBrief) {
      const contentBrief = JSON.parse(this.contentBrief)
      if (contentBrief) {
        this.filters.contentBriefId = contentBrief.id
        this.contentBriefName = contentBrief.name
      }
    } else {
      this.filters.contentBriefId = null
      this.contentBriefName = null
    }
    window.addEventListener('resize', _.debounce(this.resetItemNumber, 500), false)
    this.resetItemNumber()
  },
  data () {
    return {
      // name of content brief filter
      contentBriefName: null,
      pageItemsLimit: ITEM_NUMBER_SHRINK,
      paginationProps: { from: 0, to: ITEM_NUMBER_SHRINK },
      filters: emptyFilters,
      sortConfig: [],
      isDataUpdating: false,
      isDataFetched: false,
      sortedLinks: [],
      filteredLinks: [],
      // displayed links that was prevoiusly sorted, filtered and paginated
      paginatedLinks: [],
      totalItems: 0,
      // sort config for BaseMultiSelectSortableHeader component. Sort only by date
      dataSortConfig: {
        label: 'Date Sort',
        property: 'target_date',
        isSortable: true,
        valuePreFormatter: (timestamp) => {
          if (!timestamp) {
            return null
          }
          return moment(timestamp).valueOf()
        }
      }
    }
  },
  computed: {
    ...mapState('links', ['links']),
    urls () {
      let urls = this.links.filter(item => Boolean(item.url.name))
        .map(item => ({
          label: item.url.name,
          value: item.url.name
        }))
      return _.uniqBy(urls, 'label')
    },
    isNoData () {
      return !this.links || !this.links.length
    },
    hasPagination () {
      return this.totalItems > this.pageItemsLimit
    },
    isExportButtonVisible () {
      return this.isDataFetched && this.sortedLinks && this.sortedLinks.length
    }
  },
  watch: {
    selectedClient: {
      handler (client) {
        if (!client) {
          return
        }
        this.resetPageData()
        this.loadData()
      }
    },
    links (links) {
      this.updateSortedList()
    },
    filters: {
      handler () {
        this.updateSortedList()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('links', [
      'getLinks'
    ]),
    /**
     * pagination callback
     * @param from list element
     * @param to list element
     */
    pageChanged ({ from, to }) {
      this.paginationProps = { from, to }
      this.updatePaginatedList()
    },
    async loadData () {
      await this.getLinks({ clientId: this.clientId })
      this.isDataFetched = true
    },
    clearContentBriefFilter () {
      this.filters.contentBriefId = null
      this.contentBriefName = null
    },
    resetPageData () {
      this.filters = emptyFilters
      this.isDataFetched = false
      this.sortConfig = []
      this.filteredLinks = []
      this.paginatedLinks = []
      this.sortedLinks = []
      this.totalItems = 0
    },
    /**
     * sort callback for {@link BaseMultiSelectSortableHeader} component
      */
    onSortByDate (direction, column) {
      this.changeSorting(direction, column)
    },
    /**
     * sort logic
     * @param direction - sort direction
     * @param columnConfig - column data. On this page we have only one sorted column - date
     */
    changeSorting (direction, columnConfig) {
      const propName = columnConfig.property
      const currentConfigIndex = this.sortConfig.findIndex(config => {
        return config.propertyName === propName
      })
      if ((!this.sortConfig.length || currentConfigIndex < 0) && direction !== 'clear') {
        const newSortConfig = this.getNewSortConfig(columnConfig, direction)
        this.sortConfig = [...this.sortConfig, newSortConfig]
        this.updateSortedList()
        return
      }
      if (direction === 'clear') {
        this.sortConfig.splice(currentConfigIndex, 1)
      } else {
        this.sortConfig[currentConfigIndex].sortDirection = direction
        this.sortConfig = [...this.sortConfig]
      }
      this.updateSortedList()
    },
    /**
     * filter callback for {@link BaseMultiSelectSortableHeader}
     * @param date_from
     * @param date_to
     */
    onFilterByDate ({ date_from, date_to }) {
      this.filters.dateFrom = date_from
      this.filters.dateTo = date_to
      this.updateFilteredList()
    },
    /**
     * search by url callback
     */
    onSearch () {
      this.updateFilteredList()
    },
    /**
     * callback for clear saerch button
     */
    clearSearch () {
      this.filters.searchUrl = {}
      const search = this.$refs['search']
      if (search) {
        search.clear()
      }
      this.updateFilteredList()
    },
    /**
     * method contains sort logic
     */
    updateSortedList () {
      const sortedList = this.sortConfig.length ? helper.sortByMultipleParams(this.links, this.sortConfig) : this.links
      this.sortedLinks = []
      this.sortedLinks.push(...sortedList)
      this.updateFilteredList()
    },
    /**
     * callback on block resize
     */
    resizeHandler (event, elementWidth = 300) {
      const width = event.detail.width
      const linksCountPerRow = ~~(width / elementWidth)
      const freeSpace = width % elementWidth
      if (freeSpace < linksCountPerRow * 10 && elementWidth === 300) {
        this.resizeHandler(event, 250)
      } else {
        const gridGap = freeSpace / linksCountPerRow
        if (elementWidth === 250) {
          this.$refs.grid.classList.add('links-building__grid-small')
        } else {
          this.$refs.grid.classList.remove('links-building__grid-small')
        }
        if (gridGap >= 10 && gridGap <= 30) {
          this.$refs.grid.style.gridGap = gridGap + 'px'
        } else {
          this.$refs.grid.style.gridGap = '30px'
        }
      }
    },
    /**
     * methid contains filter logic
     */
    updateFilteredList () {
      let list = []
      list = this.sortedLinks.filter(item => {
        const isActiveStateAllowed = this.filters.isActiveState ? item['is_active'] : true
        const isNameAllowed = this.filters.searchUrl.value ? item.url.name &&
          item.url.name.includes(this.filters.searchUrl.value) : true
        const isDataAllowed = this.filters.dateFrom && this.filters.dateTo ? moment(item.target_date).isValid() &&
          moment(item.target_date).isBetween(this.filters.dateFrom, this.filters.dateTo) : true
        const isContentBriefAllowed = this.filters.contentBriefId ? item.content_brief_id === this.filters.contentBriefId : true
        return isNameAllowed && isDataAllowed && isActiveStateAllowed && isContentBriefAllowed
      })
      this.totalItems = list.length
      this.filteredLinks = []
      this.filteredLinks.push(...list)
      this.updatePaginatedList()
      setTimeout(this.resetItemNumber, 100)
    },
    /**
     * update paginated list that was previously sorted and filtered
     */
    updatePaginatedList () {
      const list = this.filteredLinks.slice(this.paginationProps.from, this.paginationProps.to)
      this.paginatedLinks = []
      this.paginatedLinks.push(...list)
    },
    getNewSortConfig (columnConfig, direction = helper.DIRECTION_ASC) {
      return {
        propertyName: columnConfig.property,
        sortDirection: direction,
        valuePreFormatter: columnConfig.valuePreFormatter || null
      }
    },
    /**
     * logic for adjust number of items in grid to window size to avoid gaps in rows
     *
     */
    resetItemNumber () {
      const grid = document.getElementsByClassName('links-building__grid')
      const width = grid && grid.length ? grid[0].clientWidth : null
      if (!width) {
        return
      }
      const gridGap = 20
      const gridItemWidth = 300
      if (width >= gridItemWidth * 5 + gridGap * 4) {
        this.pageItemsLimit = ITEM_NUMBER_FULL
      } else {
        this.pageItemsLimit = ITEM_NUMBER_SHRINK
      }
      const isShrinked = this.paginationProps.to - this.paginationProps.from === ITEM_NUMBER_SHRINK
      if ((isShrinked && this.pageItemsLimit === ITEM_NUMBER_FULL) || (!isShrinked && this.pageItemsLimit === ITEM_NUMBER_SHRINK)) {
        this.paginationProps.to = this.paginationProps.from + this.pageItemsLimit
        this.updatePaginatedList()
      }
    },

    exportCsv () {
      if (this.isDataUpdating) {
        return
      }
      const data = []
      const header = ['"Link"', '"Description"', '"Category"', '"Status"', '"Target date"', '"URL"', '"Domain authority"', '"Is Active"']
      data.push(header)
      const columnNames = ['link_url', 'description', 'category.label', 'status.label', 'target_date', 'url.name', 'domain_authority', 'is_active']
      this.filteredLinks.forEach(item => {
        const row = []
        columnNames.forEach(name => {
          if (name === 'target_date') {
            row.push(`"${filters.formattedDate(item[name])}"`)
          } else {
            row.push(`"${_.get(item, name, '')}"`)
          }
        })
        data.push(row)
      })
      csvExport(`${this.selectedClient.name.replace(/ /g, '_')}_LinkBuilding_${moment().format('YYYY-MM-DD')}`, data)
    }
  }
}
</script>

<style scoped lang="scss">
  .links-building {
    padding-top: 15px;

    &__title {
      position: absolute;
      top: 80px;
      right: 50px;
    }

    &__grid {
      margin-top: 30px;
      display: grid;
      grid-template-columns: repeat(auto-fit, 300px);
      grid-gap: 30px;
    }

    &__grid-small {
      grid-template-columns: repeat(auto-fit, 250px) !important;
    }

    &__item {

    }

    &__wrapper {

    }
    &__checkbox{
      height: fit-content;
    }
    &__date-sort{
      cursor: pointer;
    }

    &__header {
      display: grid;
      grid-template-columns: 630px 100px 260px;
      grid-gap: 30px;
      align-items: center;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px);
    }

    &__search {
      margin-right: 15px;
      font-size: 20px;
      cursor: pointer;
    }

    &__no-data {
      display: flex;
      flex: 1 1 100%;
      height: 300px;
      background-color: #fff;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__pagination-wrapper {
      display: flex;
      height: 60px;
      margin-top: 10px;
      justify-content: center;
      align-items: center;
      border: 1px solid $rfx-light-steel;
      border-radius: 4px;
    }

    &__clear-button {
      cursor: pointer;
      padding: 10px 20px;
      position: relative;
      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 20px;
        top: 22px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }

</style>
